import React, { useState, useEffect } from 'react';
import { AllieModalV2 } from '../../../components/ModalV2';
import { CitationModalProps } from './CitationModal.types';
import {
  ModalContent,
  MetadataSection,
  MetadataRow,
  MetadataLabel,
  MetadataValue,
  PreviewSection,
  PreviewPlaceholder,
  NavigationControls,
  NavigationButton,
} from './CitationModal.styles';

export const CitationModal: React.FC<CitationModalProps> = ({ 
  isOpen, 
  citation, 
  onClose,
  labels,
  renderPreview,
}) => {
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState<number | undefined>(citation?.totalPages);

  // Resetear al primer página citada cuando cambia la citation
  useEffect(() => {
    if (citation && citation.pages.length > 0) {
      setCurrentPageNumber(citation.pages[0]);
      setTotalPages(citation.totalPages);
    }
  }, [citation?.segment_id]);

  // No renderizar NADA si el modal no está abierto o faltan datos requeridos
  if (!isOpen || !citation || !labels) {
    return null;
  }

  // Callback para recibir totalPages desde CitationPreviewWithImages
  const handleTotalPagesReceived = (receivedTotalPages: number) => {
    setTotalPages(receivedTotalPages);
  };

  // Usar totalPages del estado (que puede ser actualizado por el preview)
  const effectiveTotalPages = totalPages || 1;
  const hasMultiplePages = effectiveTotalPages > 1;

  const handlePreviousPage = () => {
    if (currentPageNumber > 1) {
      setCurrentPageNumber(currentPageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPageNumber < effectiveTotalPages) {
      setCurrentPageNumber(currentPageNumber + 1);
    }
  };

  // Extract filename from URI
  const filename = citation.uri.split('/').pop() || citation.title;

  const pagesText = citation.pages.length === 1
    ? `${labels.page} ${citation.pages[0]}`
    : `${labels.pages} ${citation.pages.join(', ')}`;

  // Preparar props para el preview
  const previewProps = {
    currentPageNumber,
    currentPage: currentPageNumber,
    citation,
    labels,
    onPreviousPage: handlePreviousPage,
    onNextPage: handleNextPage,
    onTotalPagesReceived: handleTotalPagesReceived,
  };

  // Renderizar contenido del modal
  const modalContent = (
    <ModalContent>
      <MetadataSection>
        <MetadataRow>
          <MetadataLabel>{labels.documentLabel}</MetadataLabel>
          <MetadataValue>{filename}</MetadataValue>
        </MetadataRow>
        {citation.pages.length > 0 && (
          <MetadataRow>
            <MetadataLabel>{labels.pagesLabel}</MetadataLabel>
            <MetadataValue>{pagesText}</MetadataValue>
          </MetadataRow>
        )}
      </MetadataSection>

      <PreviewSection>
        {renderPreview ? renderPreview(previewProps) : (
          <>
            <PreviewPlaceholder>
              {labels.pagePreview} {currentPageNumber}
              <br />
              ({labels.noPreview})
            </PreviewPlaceholder>
            {hasMultiplePages && (
              <NavigationControls>
                <NavigationButton
                  onClick={handlePreviousPage}
                  disabled={currentPageNumber === 1}
                  aria-label={labels.previousPage}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="#51525C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </NavigationButton>
                <MetadataValue>
                  {currentPageNumber} / {effectiveTotalPages}
                </MetadataValue>
                <NavigationButton
                  onClick={handleNextPage}
                  disabled={currentPageNumber === effectiveTotalPages}
                  aria-label={labels.nextPage}
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="#51525C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </NavigationButton>
              </NavigationControls>
            )}
          </>
        )}
      </PreviewSection>
    </ModalContent>
  );

  return (
    <AllieModalV2
      isOpen={isOpen}
      handleClose={onClose}
      title={labels.modalTitle}
      closeBtnText={labels.modalCloseButton}
      width="750px"
      modalStyle={{ maxHeight: '90vh', overflowY: 'auto' }}
    >
      {modalContent}
    </AllieModalV2>
  );
};

CitationModal.displayName = 'CitationModal';

